import React, { Component } from 'react';
import { withRouter } from 'react-router';

import classNames from 'classnames';
import { DataTable } from 'components/dataTable';
import DataTableHeaderCell from 'components/dataTable/DataTableHeaderCell';
import { ProgressBar } from 'components/progressBar';
import { I18n } from 'components/translate';
import { UserInfo } from 'components/user';
import { ROUTES } from 'configs/routes/constants';
import DateHelper, { shortFormatDate } from 'helpers/Date';
import upperFirst from 'lodash/upperFirst';
import { observer, inject } from 'mobx-react';
import { AuthService } from 'modules/auth';
import {
  UserManagerService,
  UserManagerStore,
  DeepSearchService
} from 'modules/userManager';
import {
  COLUMNS_LIST,
  ENGAGEMENT_LABELS,
  ACTIONS,
  USER_STATUS
} from 'modules/userManager/constants';
import PropTypes from 'prop-types';

import UserReasonBlockModal from './actions/components/UserReasonBlockModal';
import { changeStatus } from './actions/helpers/changeStatus';
import { handleError } from './actions/helpers/handleError';
import UsersSingleActions from './actions/UsersSingleActions';
import TooltipReason from './components/TooltipReason';

import { isToggleReasonModalEnabled } from '../usersList/actions/helpers/toggleReasonModal';

import './UsersData.scss';

const progress = {
  fiftyPercent: 50,
  seventyPercent: 70,
  hundredPercent: 100
};

@inject((stores) => ({
  selectAll: stores.UserManagerStore.selectAll,
  isMobile: stores.UiStore.isMobile
}))
@withRouter
@observer
export default class UsersData extends Component {
  static propTypes = {
    history: PropTypes.object,
    selectAll: PropTypes.bool,
    isMobile: PropTypes.bool
  };

  state = {
    loading: false,
    isReasonBlockModalOpen: false,
    consumerSelected: null
  };

  dataTableRef = React.createRef();
  columnsList = Object.values(COLUMNS_LIST);

  constructor(props) {
    super(props);

    this.fetchItems = this.fetchItems.bind(this);
    this.updateItems = this.updateItems.bind(this);
    this.updateContent = this.updateContent.bind(this);
    this.onChangeSort = this.onChangeSort.bind(this);
    this.updateChangeStatus = this.updateChangeStatus.bind(this);
  }

  componentDidMount() {
    const { content } = UserManagerStore;

    UserManagerService.fetchUsersManagerConfigs();
    this.updateItems();

    if (!content.length) {
      this.fetchItems();
    }
  }

  componentWillUnmount() {
    const actualPath = AuthService.getUrlPathWithoutBase();

    if (actualPath && !actualPath.startsWith(ROUTES.USER.LIST)) {
      DeepSearchService.clearFilters();
      DeepSearchService.closeDeepSearch();
      DeepSearchService.cleanSimpleFilter();
    }
  }

  progressClass = (userProgress) =>
    classNames('user__progress', {
      'user__progress--complete': userProgress === progress.hundredPercent
    });

  updateItems(page) {
    if (this.dataTableRef && this.dataTableRef.current) {
      this.dataTableRef.current.updateItems(page);
    }
  }

  setCheckedContents(selectAll) {
    const { content } = UserManagerStore;

    UserManagerStore.content = content.map((item) => ({
      ...item,
      _checked:
        selectAll || UserManagerStore.selectedUsersListIds.includes(item.id)
    }));
  }

  async fetchItems(dataTableParams = {}) {
    const { selectAll } = this.props;

    if (Object.keys(dataTableParams).length) {
      this.setState({ loading: true });

      UserManagerStore.page = dataTableParams.pagination
        ? dataTableParams.pagination.page
        : 0;
      UserManagerStore.perPage =
        dataTableParams.perPage || UserManagerStore.perPage;

      await UserManagerService.searchUser({ selectAll });

      this.setCheckedContents(selectAll);

      this.setState({ loading: false });
    }
  }

  onChangeSort(sortOption) {
    const { userName } = UserManagerStore.sortOptions || { userName: 'asc' };
    const newSortOptions = { userName, ...sortOption };

    UserManagerStore.sortOptions = newSortOptions;
    return newSortOptions;
  }

  updateSelectedUsers(newItems) {
    newItems.forEach((item) => {
      const itemSelectedIndex = UserManagerStore.selectedUsersListIds.indexOf(
        item.id
      );
      const isItemSelected = itemSelectedIndex > -1;

      if (item._checked && !isItemSelected) {
        UserManagerStore.selectedUsersListIds.push(item.id);
      } else if (!item._checked && isItemSelected) {
        UserManagerStore.selectedUsersListIds.splice(itemSelectedIndex, 1);
      }
    });
  }

  updateContent({ newItems, selectAll }) {
    UserManagerService.setData({ selectAll });

    if (selectAll) {
      UserManagerStore.selectedUsersListIds = [];
    } else {
      this.updateSelectedUsers(newItems);
    }

    UserManagerService.setData({ content: newItems });
  }

  getColumnClass = (value) =>
    value === 'userName' ? 'user-list__user-info' : 'user-list__column-header';

  getCellClass = (value) =>
    value === 'userName' ? 'user-list__user-info' : 'user-list__column-data';

  getFormattedDate = (date) =>
    date ? DateHelper.formatDate(date, shortFormatDate) : '-';

  isBlockedByOwner(userStatus) {
    if (userStatus) {
      const { BLOCKED_BY_OWNER, BLOCKED_BY_OWNER_V2 } = USER_STATUS;

      return userStatus === BLOCKED_BY_OWNER ? BLOCKED_BY_OWNER_V2 : userStatus;
    }
  }

  renderLastAccess(item) {
    const { userLastAccessDate } = item;

    return this.getFormattedDate(userLastAccessDate);
  }

  renderPurchaseDate(item) {
    const { userPurchaseDate } = item;

    return this.getFormattedDate(userPurchaseDate);
  }

  renderFirstAccess(item) {
    const { userFirstAccessDate } = item;

    return this.getFormattedDate(userFirstAccessDate);
  }

  renderTooltipReason({
    userStatus,
    reasonBlocked,
    descriptionReasonBlocked = null
  }) {
    const blockByOwnerV2 = this.isBlockedByOwner(userStatus);

    return (
      <TooltipReason
        reason={{
          type: reasonBlocked,
          ...(descriptionReasonBlocked && { text: descriptionReasonBlocked })
        }}
      >
        <I18n
          label={UserManagerService.getStatusLabel(
            blockByOwnerV2 || userStatus
          )}
        />
      </TooltipReason>
    );
  }

  hasPlusAccess(userPlusAccess) {
    if (UserManagerService.hasPlusAccess(userPlusAccess)) {
      return <div className="plus__access">{'Plus'}</div>;
    }
  }

  renderStatusInfo({
    userStatus,
    reasonBlocked,
    descriptionReasonBlocked = null
  }) {
    const { isMobile } = this.props;
    const blockByOwnerV2 = this.isBlockedByOwner(userStatus);
    const { CHANGED_BY_OWNER, ACTIVE } = USER_STATUS;

    const label = (
      <I18n label={UserManagerService.getStatusLabel(blockByOwnerV2)} />
    );

    if (!isToggleReasonModalEnabled() || reasonBlocked === CHANGED_BY_OWNER) {
      return <div className="user-list__info-data">{label}</div>;
    }

    const showTooltip =
      !isMobile && userStatus !== ACTIVE && blockByOwnerV2 && reasonBlocked;

    return (
      <div className="user-list__info-data">
        {showTooltip
          ? this.renderTooltipReason({
              userStatus,
              reasonBlocked,
              descriptionReasonBlocked
            })
          : label}
      </div>
    );
  }

  renderStatus(item) {
    const { userStatus, userPlusAccess } = item;

    return (
      <>
        {userStatus && this.renderStatusInfo(item)}
        {userPlusAccess && this.hasPlusAccess(userPlusAccess)}
      </>
    );
  }

  renderReasonBlockedDescription(descriptionReasonBlocked) {
    return <span>{descriptionReasonBlocked}</span>;
  }

  renderReason(item) {
    const { userStatus, reasonBlocked, descriptionReasonBlocked } = item;
    const { BLOCKED_BY_OWNER, ACTIVE, CHANGED_BY_OWNER } = USER_STATUS;
    const isBlockedByOwner = userStatus === BLOCKED_BY_OWNER;

    if (
      userStatus === ACTIVE ||
      !reasonBlocked ||
      !isBlockedByOwner ||
      reasonBlocked === CHANGED_BY_OWNER
    ) {
      return null;
    }

    if (descriptionReasonBlocked) {
      return this.renderReasonBlockedDescription(descriptionReasonBlocked);
    }

    return <I18n label={UserManagerService.getReasonLabel(reasonBlocked)} />;
  }

  renderAccessCount = (item) => item.userAccessCount;

  renderEngagement = (item) => {
    const engagementValue = item[COLUMNS_LIST.ENGAGEMENT.value];

    return <I18n label={ENGAGEMENT_LABELS[engagementValue]} />;
  };

  renderProgress(item) {
    const { completedPercentage } = item.progress;

    return (
      <div className={this.progressClass(item.progress.completedPercentage)}>
        <div>
          {completedPercentage ? completedPercentage.toFixed() : 0}
          {'%'}
        </div>
        <ProgressBar progress={item.progress.completedPercentage} />
      </div>
    );
  }

  renderUserName(item) {
    return (
      <UserInfo
        name={item.userName}
        email={item.userEmail}
        pictureThumbId={item.userPictureThumbId}
        pictureFileKey={item.userPictureFileKey}
        role={UserManagerService.getRoleLabel(item.userRole)}
      />
    );
  }

  shouldRedirect(target) {
    const className = target && target.getAttribute('class');
    const targetType = target && target.nodeName.toLowerCase();
    const id = target && target.id;
    const actions = Object.keys(ACTIONS);
    const canBeClickedWithoutRedirect = ['input', 'button', 'a', 'i'];

    const isCheckbox = className === 'check';
    const isModal = className?.includes('modal');
    const isActionTag = canBeClickedWithoutRedirect.includes(targetType);
    const isUserAction = actions.includes(id);

    return !(isCheckbox || isActionTag || isUserAction || isModal);
  }

  handleRowClick = (item, { target = {} }) => {
    if (this.shouldRedirect(target)) {
      this.props.history.push(ROUTES.USER.DETAIL.replace(':userId', item.id));
    }
  };

  toggleUserReasonBlockModal = (open = null) => {
    this.setState((prevState) => ({
      isReasonBlockModalOpen:
        open !== null ? open : !prevState.isReasonBlockModalOpen
    }));
  };

  get columns() {
    const { isMobile } = this.props;
    const { columns } = UserManagerStore;
    const REASON = 4;
    const userNameIndex = 0;
    const userNameColumn = this.columnsList[userNameIndex];

    let changeableColumns = this.columnsList.filter((item) =>
      columns.includes(item.value)
    );

    if (!isMobile) {
      changeableColumns = changeableColumns.filter(
        (item) => item.order !== REASON
      );
    }

    return [userNameColumn].concat(changeableColumns).map((column) => ({
      ...column,
      className: this.getColumnClass(column.value)
    }));
  }

  renderTableBody(content, columns) {
    return (
      <DataTable.Body>
        {content.map((item) => (
          <DataTable.Row
            key={item.id}
            onClick={(event) => this.handleRowClick(item, event)}
          >
            <DataTable.SelectItem item={item} />

            <DataTable.DataColumns>
              {columns.map((column) => {
                const method = `render${upperFirst(column.value)}`;

                return (
                  this[method] && (
                    <DataTable.Cell
                      columnName={
                        column.value !== 'userName' ? column.label : null
                      }
                      className={this.getCellClass(column.value)}
                    >
                      {this[method](item)}
                    </DataTable.Cell>
                  )
                );
              })}
            </DataTable.DataColumns>

            <DataTable.Cell className="user-list__actions-header">
              <UsersSingleActions
                onUpdate={this.fetchItems}
                user={item}
                toggleUserReasonBlockModal={this.toggleUserReasonBlockModal}
              />
            </DataTable.Cell>
          </DataTable.Row>
        ))}
      </DataTable.Body>
    );
  }

  get renderTableHeader() {
    return (
      <DataTable.Header>
        <DataTable.Row>
          <DataTable.SelectAll />
          <DataTable.Header.Columns />
          <DataTableHeaderCell className="user-list__actions-header" />
        </DataTable.Row>
      </DataTable.Header>
    );
  }

  async updateChangeStatus(form) {
    await changeStatus(form, this.fetchItems);
    this.toggleUserReasonBlockModal(false);
  }

  render() {
    const { selectAll } = this.props;
    const { loading, isReasonBlockModalOpen } = this.state;
    const { page, perPage, totalElements, content } = UserManagerStore;
    const pagination = { page, perPage, totalElements };
    const columns = this.columns;

    return (
      <>
        <DataTable
          columns={columns}
          fetchItems={this.fetchItems}
          items={content.toJSON()}
          loading={loading}
          pagination={pagination}
          ref={this.dataTableRef}
          onSelect={this.updateContent}
          onChangeSort={this.onChangeSort}
          selectAll={selectAll}
          emptyList={{
            label: 'users_manager.users_list.empty',
            checkOnlineEvent: true,
            icon: 'icon-empty'
          }}
        >
          {this.renderTableHeader}

          {this.renderTableBody(content, columns)}

          <DataTable.Footer />
        </DataTable>
        {isReasonBlockModalOpen && (
          <UserReasonBlockModal
            isOpen={isReasonBlockModalOpen}
            handleError={handleError}
            toggleModal={this.toggleUserReasonBlockModal}
            changeStatus={this.updateChangeStatus}
          />
        )}
      </>
    );
  }
}
